import React from "react";
import { Users, TrendingUp, Dumbbell, Utensils, Calendar, Bell, Search, MoreHorizontal } from "lucide-react";

/**
 * High-fidelity SVG-style dashboard mockup showcasing the app.
 * Purely decorative — no external images needed.
 */
export default function DashboardMockup() {
  return (
    <div className="rounded-2xl bg-[#0A1020] border border-white/10 shadow-2xl overflow-hidden" data-testid="dashboard-mockup">
      {/* Browser chrome */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-[#08101F]">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
        </div>
        <div className="flex-1 flex justify-center">
          <div className="text-[11px] text-white/40 bg-white/5 rounded-md px-3 py-1 font-mono">app.fittrackcoach.in / dashboard</div>
        </div>
      </div>

      <div className="grid grid-cols-12 min-h-[380px] md:min-h-[520px]">
        {/* Sidebar */}
        <aside className="hidden md:flex col-span-2 flex-col gap-1 p-3 border-r border-white/5 bg-[#070D1B]">
          <div className="px-3 py-2 text-[10px] text-white/30 uppercase tracking-widest">Menu</div>
          {[
            { icon: TrendingUp, label: "Dashboard", active: true },
            { icon: Users, label: "Clients" },
            { icon: Dumbbell, label: "Workouts" },
            { icon: Utensils, label: "Diet Plans" },
            { icon: Calendar, label: "Schedule" },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <div
                key={i}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] ${
                  item.active ? "bg-cyan-500/10 text-cyan-400 border border-cyan-500/20" : "text-white/60"
                }`}
              >
                <Icon size={14} /> {item.label}
              </div>
            );
          })}
        </aside>

        {/* Main content */}
        <div className="col-span-12 md:col-span-10 p-4 md:p-6">
          {/* Top bar */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-[11px] text-white/40 uppercase tracking-wider">Good morning, Coach</div>
              <div className="text-base md:text-lg font-semibold text-white">Dashboard</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/5 text-xs text-white/50">
                <Search size={12} /> Search clients…
              </div>
              <div className="p-1.5 rounded-lg bg-white/5 border border-white/5"><Bell size={13} className="text-white/60"/></div>
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600" />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            {[
              { label: "Active Clients", value: "48", delta: "+5", color: "cyan" },
              { label: "Workouts This Week", value: "126", delta: "+18", color: "blue" },
              { label: "Goals Hit", value: "87%", delta: "+12%", color: "emerald" },
            ].map((s, i) => (
              <div key={i} className="p-3 md:p-4 rounded-xl bg-gradient-to-br from-white/[0.04] to-white/[0.01] border border-white/5">
                <div className="text-[10px] md:text-[11px] text-white/40 uppercase tracking-wider">{s.label}</div>
                <div className="mt-1 flex items-baseline gap-2">
                  <div className="text-lg md:text-2xl font-bold text-white">{s.value}</div>
                  <div className="text-[10px] md:text-xs text-emerald-400 font-semibold">↑ {s.delta}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Main grid */}
          <div className="grid grid-cols-12 gap-3">
            {/* Chart */}
            <div className="col-span-12 md:col-span-7 p-4 rounded-xl bg-white/[0.025] border border-white/5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs text-white/50">Client progress</div>
                  <div className="text-sm font-semibold text-white">Weight trend — last 30 days</div>
                </div>
                <MoreHorizontal size={14} className="text-white/30" />
              </div>
              {/* Simple SVG chart */}
              <svg viewBox="0 0 400 130" className="w-full h-28 md:h-32">
                <defs>
                  <linearGradient id="chartGrad" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#22D3EE" stopOpacity="0.4"/>
                    <stop offset="100%" stopColor="#22D3EE" stopOpacity="0"/>
                  </linearGradient>
                </defs>
                <path
                  d="M0,95 C30,90 50,70 80,75 C110,80 130,50 160,45 C190,40 210,60 240,55 C270,50 290,30 320,35 C350,40 380,25 400,22 L400,130 L0,130 Z"
                  fill="url(#chartGrad)"
                />
                <path
                  d="M0,95 C30,90 50,70 80,75 C110,80 130,50 160,45 C190,40 210,60 240,55 C270,50 290,30 320,35 C350,40 380,25 400,22"
                  fill="none"
                  stroke="#22D3EE"
                  strokeWidth="2"
                />
                {[80, 160, 240, 320].map((cx, i) => (
                  <circle key={i} cx={cx} cy={[75, 45, 55, 35][i]} r="3" fill="#38BDF8" />
                ))}
              </svg>
            </div>

            {/* Client list */}
            <div className="col-span-12 md:col-span-5 p-4 rounded-xl bg-white/[0.025] border border-white/5">
              <div className="text-xs text-white/50 mb-1">Today's sessions</div>
              <div className="text-sm font-semibold text-white mb-3">Upcoming clients</div>
              <div className="space-y-2.5">
                {[
                  { name: "Aarav Mehta", goal: "Weight loss", time: "6:30 AM", init: "AM" },
                  { name: "Priya Shah", goal: "Strength", time: "8:00 AM", init: "PS" },
                  { name: "Rohit Kumar", goal: "Muscle gain", time: "5:30 PM", init: "RK" },
                ].map((c, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-[#050814] bg-gradient-to-br from-cyan-400 to-blue-500">
                      {c.init}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-white truncate">{c.name}</div>
                      <div className="text-[10px] text-white/40">{c.goal}</div>
                    </div>
                    <div className="text-[10px] font-semibold text-cyan-400">{c.time}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
